import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {Heart, Star, MessageCircleDashed as MessageCircle, Calendar, LogOut, Home} from 'lucide-react'

export function UserDashboard() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  if (!user) {
    navigate('/')
    return null
  }

  const favoriteContent = [
    { id: 'tom-jerry-magic-ring', title: 'Tom & Jerry: The Magic Ring', url: '/review/tom-jerry-magic-ring' },
    { id: 'smurfs-magic-flute', title: 'The Smurfs and the Magic Flute', url: '/review/smurfs-magic-flute' },
    { id: 'morphle-cartoon', title: 'Morphle Cartoon', url: '/review/morphle-cartoon' }
  ]

  const userFavorites = favoriteContent.filter(content => user.favorites.includes(content.id))

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-orange-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-pink-600 hover:text-pink-700">
              <Home className="w-5 h-5" />
              <span className="font-semibold">Back to Home</span>
            </Link>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
              My Dashboard
            </h1>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline font-semibold">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Profile Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <img
              src={user.avatar}
              alt={user.username}
              className="w-24 h-24 rounded-full border-4 border-pink-200"
            />
            <div className="text-center sm:text-left">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">{user.username}</h2>
              <p className="text-gray-600 mb-2">{user.email}</p>
              <div className="flex items-center gap-2 text-sm text-gray-500 justify-center sm:justify-start">
                <Calendar className="w-4 h-4" />
                <span>Joined {new Date(user.joinedDate).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-pink-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6 text-pink-600" />
            </div>
            <p className="text-3xl font-bold text-gray-800 mb-1">{user.favorites.length}</p>
            <p className="text-gray-600">Favorites</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-yellow-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
            <p className="text-3xl font-bold text-gray-800 mb-1">
              {JSON.parse(localStorage.getItem('reviews_tom-jerry-magic-ring') || '[]').filter((r: any) => r.name === 'You').length +
               JSON.parse(localStorage.getItem('reviews_smurfs-magic-flute') || '[]').filter((r: any) => r.name === 'You').length +
               JSON.parse(localStorage.getItem('reviews_morphle-cartoon') || '[]').filter((r: any) => r.name === 'You').length}
            </p>
            <p className="text-gray-600">Reviews</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <MessageCircle className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-gray-800 mb-1">
              {JSON.parse(localStorage.getItem('magicCartoonComments') || '[]').filter((c: any) => c.userId === user.id).length}
            </p>
            <p className="text-gray-600">Comments</p>
          </div>
        </div>

        {/* Favorite Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500" />
            My Favorite Magical Cartoons
          </h3>
          
          {userFavorites.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg mb-2">No favorites yet!</p>
              <p className="mb-4">Start exploring and save your favorite magical moments.</p>
              <Link
                to="/"
                className="inline-block bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all"
              >
                Explore Content
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {userFavorites.map((content) => (
                <Link
                  key={content.id}
                  to={content.url}
                  className="group bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 hover:shadow-lg transition-all transform hover:scale-105"
                >
                  <div className="flex items-start justify-between mb-3">
                    <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                    <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2 group-hover:text-pink-600 transition-colors">
                    {content.title}
                  </h4>
                  <p className="text-sm text-gray-600">Click to view full review</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
